import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ApiErrorCode,
  parseApiError,
  permissions,
  PermissionBundleIncompleteDetailsSchema,
} from '@ooh/contracts';
import type { Role, RoleScopeRow, ScopeOwnership, OfferDepartment } from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryKey, useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import { allowDepartmentScope } from './role-codes';
import {
  countChangedPermissions,
  applyGrantUpdate,
  withGroupMediaTypeScope,
  withGroupOwnershipScope,
  withGroupDepartmentScope,
  addPermissionScopeGroup,
  removePermissionScopeGroup,
  permissionDisplayName,
  type TranslateFn,
} from './grant-helpers';

export interface GrantStagingState {
  committedGrants: RoleScopeRow[];
  grantsForSelectedRole: RoleScopeRow[];
  hasUnsavedChanges: boolean;
  changedPermissionCount: number;
  isSaving: boolean;
  resetStaged: () => void;
  handleSaveGrants: () => void;
  handleDiscardGrants: () => void;
  /** `groupIndex` addresses one of the permission's independent scope groups — see `groupGrantsByScope`. */
  handleChangeMediaTypes: (
    permissionCode: string,
    groupIndex: number,
    mediaTypeCodes: string[] | null,
  ) => void;
  handleChangeOwnership: (
    permissionCode: string,
    groupIndex: number,
    ownership: ScopeOwnership | null,
  ) => void;
  handleChangeDepartment: (
    permissionCode: string,
    groupIndex: number,
    department: OfferDepartment | null,
  ) => void;
  /** Adds a scope group — the initial grant when ungranted, "Dodaj zakres" once already granted. */
  handleAddScope: (permissionCode: string) => void;
  /** Removes one scope group; removing the last one revokes the permission entirely (bundle-aware). */
  handleRemoveScope: (permissionCode: string, groupIndex: number) => void;
}

type UpdateGrants = (
  permissionCode: string,
  updater: (grants: RoleScopeRow[]) => RoleScopeRow[],
) => void;

/** Applies a transform to the FULL staged grants array (not isolated to one permission). */
type UpdateAllGrants = (transform: (grants: RoleScopeRow[]) => RoleScopeRow[]) => void;

/**
 * Builds the per-dimension + add/remove grant-editing handlers `GrantRow`
 * wires up. Add/remove use `updateAllGrants` (not `updatePermissionGrants`)
 * because the granted<->ungranted transition can span more than one
 * permission code (see `PERMISSION_BUNDLES`) and must be cascaded atomically
 * across the whole array; per-dimension edits stay isolated to one
 * permission's rows via `updatePermissionGrants`.
 */
function createGrantHandlers(
  updatePermissionGrants: UpdateGrants,
  updateAllGrants: UpdateAllGrants,
) {
  return {
    handleChangeMediaTypes: (
      permissionCode: string,
      groupIndex: number,
      mediaTypeCodes: string[] | null,
    ) =>
      updatePermissionGrants(permissionCode, (grants) =>
        withGroupMediaTypeScope(grants, permissionCode, groupIndex, mediaTypeCodes),
      ),
    handleChangeOwnership: (
      permissionCode: string,
      groupIndex: number,
      ownership: ScopeOwnership | null,
    ) =>
      updatePermissionGrants(permissionCode, (grants) =>
        withGroupOwnershipScope(grants, groupIndex, ownership),
      ),
    handleChangeDepartment: (
      permissionCode: string,
      groupIndex: number,
      department: OfferDepartment | null,
    ) =>
      updatePermissionGrants(permissionCode, (grants) =>
        withGroupDepartmentScope(grants, groupIndex, department),
      ),
    handleAddScope: (permissionCode: string) =>
      updateAllGrants((grants) =>
        addPermissionScopeGroup(grants, permissionCode, allowDepartmentScope(permissionCode)),
      ),
    handleRemoveScope: (permissionCode: string, groupIndex: number) =>
      updateAllGrants((grants) => removePermissionScopeGroup(grants, permissionCode, groupIndex)),
  };
}

/**
 * Builds the PERMISSION_BUNDLE_INCOMPLETE toast message. Prefers the
 * specific "X (code) requires Y (code)" message built from the server's
 * structured `details` payload (see `PermissionBundleIncompleteDetailsSchema`
 * in `@ooh/contracts`) so the admin knows exactly which permissions
 * conflict — falls back to the generic translated message when `details` is
 * missing or malformed (e.g. an older API build) so the toast is never blank.
 */
function bundleIncompleteMessage(t: TranslateFn, details: unknown): string {
  const parsed = PermissionBundleIncompleteDetailsSchema.safeParse(details);
  if (
    !parsed.success ||
    parsed.data.grantedCodes.length === 0 ||
    parsed.data.missingCodes.length === 0
  ) {
    return t('permissionMatrix.saveBar.bundleIncomplete');
  }
  const label = (code: string) => `${permissionDisplayName(t, code)} (${code})`;
  return t('permissionMatrix.saveBar.bundleIncompleteDetailed', {
    grantedNames: parsed.data.grantedCodes.map(label).join(', '),
    missingNames: parsed.data.missingCodes.map(label).join(', '),
  });
}

/** Wraps the scopes-replace mutation with cache invalidation + success/error toasts. */
function useSaveGrantsMutation(onSaved: () => void, onPermissionsSaved: (() => void) | undefined) {
  const { t } = useTranslation('admin');
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, scopes }: { roleId: number; scopes: RoleScopeRow[] }) =>
      api.call(permissions.updateRoleScopes, { id: roleId, scopes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiQueryKey(permissions.getRoleScopes) });
      onPermissionsSaved?.();
      onSaved();
      toast.success(t('permissionMatrix.saveBar.saved'));
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      // PERMISSION_BUNDLE_INCOMPLETE gets a dedicated translated toast rather
      // than the generic parsed message — per packages/contracts/AGENTS.md's
      // "translated onError handler for every new ApiErrorCode" MUST rule.
      // The client-side bundle cascade (grantPermissionWithBundle /
      // revokePermissionWithBundle) normally prevents this from ever
      // reaching the server; this handler is defense in depth.
      if (parsed.errorCode === ApiErrorCode.PERMISSION_BUNDLE_INCOMPLETE) {
        toast.error(bundleIncompleteMessage(t as TranslateFn, parsed.details));
        return;
      }
      toast.error(apiErrorToUserMessage(parsed, 'mutation'));
    },
  });
}

/**
 * Owns the staged-edit batch-save grant editor for the currently selected
 * role: committed grants (server), staged grants (local, `null` = no unsaved
 * changes), the dimension-change handlers `GrantRow` wires up, and the save
 * mutation. Mount it keyed by role id so switching roles drops staged edits.
 */
export function useGrantStaging(
  selectedRole: Role,
  selectedIsImmutable: boolean,
  onPermissionsSaved?: () => void,
): GrantStagingState {
  const [stagedGrants, setStagedGrants] = useState<RoleScopeRow[] | null>(null);
  const { data: committedGrants } = useApiSuspenseQuery(permissions.getRoleScopes, {
    id: selectedRole.id,
  });

  function resetStaged() {
    setStagedGrants(null);
  }

  function updatePermissionGrants(
    permissionCode: string,
    updater: (grants: RoleScopeRow[]) => RoleScopeRow[],
  ) {
    if (selectedIsImmutable) return;
    setStagedGrants((prevStaged) => {
      const base = prevStaged ?? committedGrants;
      return applyGrantUpdate(base, permissionCode, updater);
    });
  }

  function updateAllGrants(transform: (grants: RoleScopeRow[]) => RoleScopeRow[]) {
    if (selectedIsImmutable) return;
    setStagedGrants((prevStaged) => transform(prevStaged ?? committedGrants));
  }

  const saveGrantsMutation = useSaveGrantsMutation(resetStaged, onPermissionsSaved);

  function handleSaveGrants() {
    if (stagedGrants === null) return;
    saveGrantsMutation.mutate({ roleId: selectedRole.id, scopes: stagedGrants });
  }

  const grantsForSelectedRole = stagedGrants ?? committedGrants;
  const changedPermissionCount =
    stagedGrants !== null ? countChangedPermissions(committedGrants, stagedGrants) : 0;

  return {
    committedGrants,
    grantsForSelectedRole,
    hasUnsavedChanges: !selectedIsImmutable && changedPermissionCount > 0,
    changedPermissionCount,
    isSaving: saveGrantsMutation.isPending,
    resetStaged,
    handleSaveGrants,
    handleDiscardGrants: resetStaged,
    ...createGrantHandlers(updatePermissionGrants, updateAllGrants),
  };
}
